import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import usePresence from "@convex-dev/presence/react";
import { Hash, Search } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageList } from "./MessageList";
import { MessageComposer } from "./MessageComposer";
import { TypingIndicator } from "./TypingIndicator";

export function ConversationView({
  conversationId,
  meId,
}: {
  conversationId: Id<"conversations">;
  meId: Id<"users">;
}) {
  const data = useQuery(api.conversations.get, { conversationId });
  const markRead = useMutation(api.conversations.markRead);
  const [searchOpen, setSearchOpen] = useState(false);

  const presence = usePresence(
    api.presence,
    `conversation:${conversationId}`,
    meId,
  );

  useEffect(() => {
    void markRead({ conversationId });
  }, [conversationId, markRead]);

  if (data === undefined) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Conversation not found.
      </div>
    );
  }

  const { conversation, members } = data;
  const peer =
    conversation.kind === "dm"
      ? members.find((m) => m._id !== meId) ?? null
      : null;

  const onlineCount = (presence ?? []).filter((p) => p.online).length;
  const peerOnline =
    peer && (presence ?? []).some((p) => p.userId === peer._id && p.online);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 border-b px-4 h-12 shrink-0">
        {conversation.kind === "room" ? (
          <>
            <Hash className="size-4 text-muted-foreground" />
            <span className="font-medium">{conversation.name ?? "Untitled"}</span>
            <span className="text-xs text-muted-foreground">
              {members.length} member{members.length === 1 ? "" : "s"} ·{" "}
              {onlineCount} online
            </span>
          </>
        ) : peer ? (
          <>
            <div className="relative">
              <Avatar className="size-7">
                {peer.image ? <AvatarImage src={peer.image} /> : null}
                <AvatarFallback>
                  {(peer.name ?? "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {peerOnline ? (
                <span className="absolute bottom-0 right-0 size-2 rounded-full bg-green-500 border border-background" />
              ) : null}
            </div>
            <span className="font-medium">{peer.name ?? "Unknown"}</span>
          </>
        ) : null}
        <div className="ml-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="Search in conversation"
          >
            <Search className="size-4" />
          </Button>
        </div>
      </div>
      {searchOpen ? (
        <ConversationSearch
          conversationId={conversationId}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
      <MessageList
        conversationId={conversationId}
        conversationKind={conversation.kind}
        meId={meId}
      />
      <TypingIndicator conversationId={conversationId} />
      <MessageComposer conversationId={conversationId} />
    </div>
  );
}

function ConversationSearch({
  conversationId,
  onClose,
}: {
  conversationId: Id<"conversations">;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery(
    api.search.inConversation,
    debounced ? { conversationId, query: debounced } : "skip",
  );

  return (
    <div className="border-b bg-muted/30 px-4 py-2">
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search in this conversation..."
          className="h-8"
        />
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      {debounced ? (
        <ScrollArea className="max-h-60 mt-2">
          {results === undefined ? (
            <p className="text-xs text-muted-foreground p-2">Searching...</p>
          ) : results.length === 0 ? (
            <p className="text-xs text-muted-foreground p-2">No matches.</p>
          ) : (
            <ul className="space-y-1 text-sm py-1">
              {results.map((r) => (
                <li
                  key={r._id}
                  className="rounded px-2 py-1 hover:bg-accent cursor-default"
                >
                  <div className="text-xs text-muted-foreground">
                    {r.author?.name ?? "Unknown"} ·{" "}
                    {new Date(r.createdAt).toLocaleString()}
                  </div>
                  <div className="text-sm line-clamp-2">{r.body}</div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      ) : null}
    </div>
  );
}
